import { getTagFromNode, GetViewInput, sendNotification, useApiClient, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  Entity,
  EntityMetadata,
  EntityModel,
  FormOpenMode,
  ListFieldDefinition,
  ListValue,
  LookupFieldDefinition,
  LookupValue,
  MultiSelectorDefinition,
  MultiSelectorVisualization,
  readBaseMetadataByName,
  readEntityModelIncludeExternal,
} from '@msdk/definitions'
import {
  BaseSelect,
  ButtonIcon,
  CloseIcon,
  FormField,
  FormFieldLabel,
  OptionItem,
  SearchIcon,
  Select,
  SelectInputAdornment,
  selectValueRenderer,
} from '@msdk/ui'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation, useViewModal } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { useEntityFromCollection } from '../../../state'
import { filtersToObject, nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

const mapValues = (
  entities: Entity[],
  fieldDef: ListFieldDefinition,
  formDef: MultiSelectorDefinition,
): OptionItem[] => {
  const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
  const selected = (entities ?? []).map((e) => e.fields[lookupDef.name]?.toStringForView() ?? '')
  return selected.map((s) => ({
    value: s,
    label: s,
  }))
}

// filters out deleted linked entities
const getSelectedRows = (entities: Entity[], lookupFieldName: string): string[] => {
  return entities
    .map((r) => r.fields[lookupFieldName] as LookupValue | null)
    .filter((v): v is LookupValue => !!v && !v.isDeleted)
    .map((v) => v.value)
    .filter((v): v is string => !!v)
}

export function MultiselectorField({ node, onChange, fieldArgs }: FieldProps) {
  const t = useTranslation()
  const api = useApiClient()
  const { createModal } = useViewModal()
  const fieldDef = node.value.fieldDefinition as ListFieldDefinition
  const formDef = node.value.formDefinition as MultiSelectorDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const [value, setValue] = useFieldValue(node, onChange)
  const { moduleName } = useParams()
  const entity = useEntityFromCollection(node.value.entityId as string)
  const [dropdownOptions, setDropdownOptions] = useState<Entity[]>([])

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const isDropdownVisualization = formDef?.visualization === MultiSelectorVisualization.Dropdown

  const loadDropdownOptions = async () => {
    try {
      const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
      const baseMetadata = readBaseMetadataByName(lookupDef.linkedEntity) as EntityMetadata
      const entityModel = readEntityModelIncludeExternal(
        lookupDef.linkedEntity,
        baseMetadata?.msProvider,
      ) as EntityModel
      const filters = filtersToObject(lookupDef.viewFilterFields, entity)

      const viewParams: GetViewInput | undefined = {
        pageSize: 30,
        filters,
      }

      const result = await api.entity.getView(baseMetadata?.msProvider, entityModel, lookupDef.viewName, viewParams)
      setDropdownOptions(result.source || [])
    } catch (error) {
      const title = t('MULTI_LOAD_ERROR', 'Multiselector Load Error')
      const desc = t('MULTI_LOAD_ERROR_DESC', 'Something went wrong while retrieving dropdown data')
      sendNotification({ title, message: desc, status: 'error' })
      console.error('Failed to load dropdown options:', error)
      setDropdownOptions([])
    }
  }

  const onClear = () => {
    node.value.isDirty = true
    setValue(null)
  }

  const onDropdownChange = async (selectedValue: string | string[]) => {
    const selectedIds = Array.isArray(selectedValue) ? selectedValue : selectedValue ? [selectedValue] : []

    if (!selectedIds || selectedIds.length === 0) {
      node.value.isDirty = true
      setValue(null)
      return
    }

    const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
    const listValue = new ListValue()
    listValue.value = selectedIds.map((id) => {
      const selectedEntity = dropdownOptions.find((e) => e.id === id)
      const dismissEntity: Entity = {
        id: crypto.randomUUID(),
        entityName: fieldDef.entityName,
        parentId: node.value.entityId as string,
        isPersisted: false,
        isInCreation: false,
        fields: {},
        canUpdate: false,
        canActivate: false,
        canDeactivate: false,
        canDelete: false,
      }

      fieldDef.fields.forEach((r) => {
        dismissEntity.fields[r.name] = null
      })

      const lookup = new LookupValue(id)
      if (selectedEntity && selectedEntity.fields) {
        const linkedFields = lookupDef.linkedFields ? lookupDef.linkedFields.split(',') : []
        lookup.entityDescription = linkedFields
          .map((f) => selectedEntity.fields[f.trim()]?.toStringForView())
          .filter(Boolean)
          .join(' ')
      }
      dismissEntity.fields[lookupDef.name] = lookup
      return dismissEntity
    })

    node.value.isDirty = true
    setValue(listValue)
  }

  const getDropdownOptions = (): OptionItem[] => {
    const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
    return dropdownOptions.map((e) => {
      const linkedFields = lookupDef.linkedFields ? lookupDef.linkedFields.split(',') : []
      const label = linkedFields
        .map((f) => e.fields[f.trim()]?.toStringForView())
        .filter(Boolean)
        .join(' ')
      return {
        value: e.id,
        label: label || e.id,
      }
    })
  }

  const getSelectedDropdownItems = (): OptionItem[] => {
    const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
    if (!value) return []
    const entities = (value as ListValue).value || []
    return entities
      .map((e) => {
        const lookupValue = e.fields[lookupDef.name] as LookupValue
        const id = lookupValue?.value
        const label = lookupValue?.entityDescription || id || ''
        return id ? { value: id, label } : null
      })
      .filter((item): item is OptionItem => item !== null)
  }

  const onSearchClick = () => {
    const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
    const modalRef = createModal(lookupDef.linkedEntity, lookupDef.viewName, onSearchDismiss, {
      lookup: true,
      multiSelect: true,
      selectedRows: getSelectedRows((value?.value as Entity[]) || [], lookupDef.name),
      prefilledFilters: filtersToObject(lookupDef.viewFilterFields, entity, node.value.listName, node.value.rowId),
    })
    modalRef.open()
  }

  const onSearchDismiss = async (selected: Entity[]) => {
    if (selected) {
      const lookupDef = fieldDef.fields.find((f) => f.name === formDef.lookup) as LookupFieldDefinition
      const listValue = new ListValue()
      listValue.value = selected.map((e) => {
        const lookup = new LookupValue(e.id)
        const dismissEntity: Entity = {
          id: crypto.randomUUID(),
          entityName: fieldDef.entityName,
          parentId: node.value.entityId as string,
          isPersisted: false,
          isInCreation: false,
          fields: {},
          canUpdate: false,
          canActivate: false,
          canDeactivate: false,
          canDelete: false,
        }

        fieldDef.fields.forEach((r) => {
          dismissEntity.fields[r.name] = null
        })
        dismissEntity.fields[lookupDef.name] = lookup

        return dismissEntity
      })

      for (let i = 0; i < listValue.value.length; i++) {
        const current = listValue.value[i]
        const lookupValue = current.fields[lookupDef.name] as LookupValue
        const baseMetadata = readBaseMetadataByName(lookupDef.linkedEntity)
        const lookup = await api.entity.getLookup(
          baseMetadata?.msProvider as string,
          lookupDef.linkedEntity,
          lookupValue.value as string,
          lookupDef,
          lookupDef.links[0],
        )
        lookupValue.entityDescription = lookup.entityDescription
      }

      node.value.isDirty = true
      setValue(listValue)
    }
  }

  const arrayValue = value ? (value as ListValue).value ?? [] : []
  const hasValue = arrayValue.length > 0
  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fieldDef, node)

  if (isDropdownVisualization) {
    return (
      <FormField
        required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
        size="small"
        data-test-id={getTagFromNode(fieldDef, moduleName)}
        severity={severity}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      >
        <FormFieldLabel shrink={hasValue}>{fieldLabel}</FormFieldLabel>
        <Select
          label={fieldLabel}
          options={getDropdownOptions()}
          value={getSelectedDropdownItems()}
          onChange={onDropdownChange}
          onOpen={() => {
            void loadDropdownOptions()
          }}
          disabled={isReadOnly}
          multi
          onClear={onClear}
        />
        <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
      </FormField>
    )
  }

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      <FormFieldLabel shrink={hasValue}>{fieldLabel}</FormFieldLabel>
      <BaseSelect
        multiple
        open={false}
        label={fieldArgs?.showLabel && hasValue && fieldLabel}
        disabled
        value={arrayValue}
        IconComponent={() => null}
        renderValue={(entities) => selectValueRenderer(mapValues(entities as Entity[], fieldDef, formDef))}
        endAdornment={
          <SelectInputAdornment>
            {!isReadOnly && (
              <ButtonIcon color="primary" aria-label="Search" onClick={onSearchClick} edge="end">
                <SearchIcon />
              </ButtonIcon>
            )}
            {value && !value?.isEmpty() && !isReadOnly && (
              <ButtonIcon color="primary" aria-label="Clear" onClick={onClear} edge="end">
                <CloseIcon />
              </ButtonIcon>
            )}
          </SelectInputAdornment>
        }
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
